import React, { useEffect, useRef } from 'react';

// A horizontally scrolling marquee that drifts on its own AND can be scrolled,
// swiped or dragged by the user. Children are rendered twice for a seamless
// loop. Auto-drift pauses while the user hovers, drags, swipes or wheels, then
// resumes shortly after. Respects prefers-reduced-motion.
//
// Performance notes (Jun 2026 - mobile scroll jank fix):
//   • The half-width is measured once (and on resize) instead of reading
//     scrollWidth every frame, which used to force a layout per frame per
//     marquee (3 on the homepage).
//   • The rAF loop is suspended while the marquee is off-screen, while the
//     tab is hidden, and - critically - while the user is scrolling the page
//     vertically. Writing scrollLeft during a vertical touch scroll is what
//     made mobile scrolling feel glitchy.
//   • pointermove/pointerup are only bound for the duration of a mouse drag.
export default function ScrollMarquee({ children, speed = 0.55, className = '', testid }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let raf = 0;
    let userPaused = false;      // hover / drag / wheel / swipe
    let offscreen = true;        // set by IntersectionObserver
    let pageScrolling = false;   // true briefly while the page scrolls vertically
    let resumeTimer = 0;
    let scrollIdleTimer = 0;
    let isDragging = false;
    let startX = 0;
    let startScroll = 0;
    let halfWidth = 0;
    let offset = 0;              // sub-pixel accumulator

    const measure = () => { halfWidth = (el.scrollWidth / 2) || 0; };

    const step = () => {
      raf = 0;
      if (!userPaused && !pageScrolling && halfWidth > 0) {
        offset += speed;
        if (offset >= 1) {
          const px = Math.floor(offset);
          offset -= px;
          let next = el.scrollLeft + px;
          if (next >= halfWidth) next -= halfWidth;
          el.scrollLeft = next;
        }
      }
      schedule();
    };

    const schedule = () => {
      if (raf || reduce || offscreen || document.hidden) return;
      raf = requestAnimationFrame(step);
    };
    const stop = () => { if (raf) { cancelAnimationFrame(raf); raf = 0; } };

    const pause = () => { userPaused = true; if (resumeTimer) { clearTimeout(resumeTimer); resumeTimer = 0; } };
    const resumeSoon = (delay = 1400) => {
      if (resumeTimer) clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => { userPaused = false; schedule(); }, delay);
    };

    const onEnter = (e) => { if (e.pointerType === 'mouse') pause(); };
    const onLeave = () => { if (!isDragging) resumeSoon(350); };
    const onWheel = () => { pause(); resumeSoon(); };
    const onTrackScroll = () => {
      if (halfWidth <= 0) return;
      if (el.scrollLeft >= halfWidth) el.scrollLeft -= halfWidth;
      else if (el.scrollLeft <= 0) el.scrollLeft += halfWidth;
    };

    const onPointerMove = (e) => {
      if (!isDragging) return;
      e.preventDefault();
      el.scrollLeft = startScroll - (e.pageX - startX);
    };
    const endDrag = () => {
      if (isDragging) { isDragging = false; el.style.cursor = 'grab'; }
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', endDrag);
      resumeSoon();
    };
    const onPointerDown = (e) => {
      pause();
      if (e.pointerType === 'mouse') {
        isDragging = true;
        startX = e.pageX;
        startScroll = el.scrollLeft;
        el.style.cursor = 'grabbing';
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', endDrag);
      } else {
        resumeSoon(1800);
      }
    };

    // Suspend the drift while the page is being scrolled vertically. This keeps
    // the main thread free so touch scrolling stays on the compositor.
    const onPageScroll = () => {
      pageScrolling = true;
      if (scrollIdleTimer) clearTimeout(scrollIdleTimer);
      scrollIdleTimer = setTimeout(() => { pageScrolling = false; }, 180);
    };

    const io = new IntersectionObserver(([entry]) => {
      offscreen = !entry.isIntersecting;
      if (offscreen) stop(); else schedule();
    }, { rootMargin: '120px 0px' });
    io.observe(el);

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);

    const onVisibility = () => { if (document.hidden) stop(); else schedule(); };

    measure();
    el.scrollLeft = 1;
    el.addEventListener('pointerenter', onEnter);
    el.addEventListener('pointerleave', onLeave);
    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('wheel', onWheel, { passive: true });
    el.addEventListener('scroll', onTrackScroll, { passive: true });
    window.addEventListener('scroll', onPageScroll, { passive: true });
    window.addEventListener('resize', measure, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      io.disconnect();
      if (ro) ro.disconnect();
      if (resumeTimer) clearTimeout(resumeTimer);
      if (scrollIdleTimer) clearTimeout(scrollIdleTimer);
      el.removeEventListener('pointerenter', onEnter);
      el.removeEventListener('pointerleave', onLeave);
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('scroll', onTrackScroll);
      window.removeEventListener('scroll', onPageScroll);
      window.removeEventListener('resize', measure);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', endDrag);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [speed]);

  return (
    <div
      ref={ref}
      data-testid={testid}
      className={`no-scrollbar overflow-x-auto overflow-y-hidden overscroll-x-contain ${className}`}
      style={{ scrollbarWidth: 'none', cursor: 'grab', touchAction: 'pan-x pan-y' }}
    >
      <div className="flex w-max">
        <div className="flex shrink-0">{children}</div>
        <div className="flex shrink-0" aria-hidden="true">{children}</div>
      </div>
    </div>
  );
}
